import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';

export interface UseNoteHandlerProps {
  onSuccess?: (id: any, noteContent: string) => void;
}

export const useNoteHandler = ({ onSuccess }: UseNoteHandlerProps = {}) => {
  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const notesService = new AttioNotesService();
  const { enqueueSnackbar } = useSnackbar();

  const handleNoteSave = async (
    id: string | number,
    noteContent: string,
    currentNote: string | undefined,
    request: CreateNoteRequest,
    hasExistingNote: boolean,
    existingNoteId?: string
  ) => {
    setIsNoteLoading(true);
    setNoteError(null);

    try {
      await notesService.createOrUpdateNote(request, hasExistingNote, existingNoteId);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(id, noteContent);
      }
      
      enqueueSnackbar('Note saved successfully!', { variant: 'success' });
    } catch (err: unknown) {
      // Error handling
      let errorMessage = 'Failed to save note. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const errorResponse = err as { response?: { data?: { message?: string } } };
        if (errorResponse.response?.data?.message) {
          errorMessage = errorResponse.response.data.message;
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        const errorWithMessage = err as { message: string };
        errorMessage = errorWithMessage.message;
      }
      
      setNoteError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsNoteLoading(false);
    }
  };

  return {
    isNoteLoading,
    noteError,
    handleNoteSave,
  };
};
